import { GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link MapComponent} */
const styles: string = css`
:host {
    height: 100%;
    width: 100%;
    
}

.container {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
}

.header {
    width: 80%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
}

.tablet {
    border: 5px solid gray;
    background-color: black;
    width: 80%;
    margin-top: 15px;
    padding: 10px;

    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    text-align: center;

}

.cell {
    height: 200px;
    background-color: #7f6ed7;
    margin: 20px;
    width: 22%;

    border-radius: 10px;
    cursor: pointer;

    display: flex;
    justify-content: center;
    align-items: center
}

.sequence-number {
    margin: auto;
    font-size: 100px;

}

.victory {
    color: yellow;
    font-size: 200px;
    margin: 0;
}

.button {
    background-color: #7f6ed7;
    border: 1px solid #332c57;
    padding: 5px 10px;
    text-transform: uppercase;
    cursor: pointer;
    display: inline-block;
    user-select: none;
}

.button:hover {
    background-color: #332c57;
}
    
`;

export class TabletComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** The level the player is in */
    private _level: number = 1;
    private _sequence: number[] = [];

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();
        this.updateGameState(state);
    }

    /**
         * Update the canvas to the provided game state
         *
         * @param state Game state to update the canvas to
         */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTablet()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    private renderTablet(): HTMLElement {
        // Make changes to frontend after its loaded
        setTimeout(() => {
            // Generate level
            this.generateLevel();

            // Go back to previous page (North Hallway) after pressing close button
            const closeDiv: HTMLDivElement = this.shadowRoot!.getElementById("close") as HTMLDivElement;
            closeDiv.addEventListener("click", () => {
                // Switch page back to canvas
                this.updateGameState({ type: "switch-page", page: Page.Canvas } as GameState);
            });
        });
        return html`
                    <section class="container">
                        <section class="header">
                            <p id="level"></p>
                            <div id="close" class="button">Close</div>
                        </section>
                                
                        <section id="tablet" class="tablet">
                        </section>
                    </section>
                    
        `;
    }

    /**
     * Restarts and regenerates the entire level and goes back to level 1.
     */
    private restartLevel(): void {
        // Generate new level after wrong click
        const tablet: HTMLDivElement = this.shadowRoot!.getElementById("tablet") as HTMLDivElement;
        tablet.innerHTML = "";

        this._level = 1;
        this.generateLevel();
        return;
    }

    /**
     * Goes to the next level or displays a victory message if level 6 is reached.
     */
    private async nextLevel(): Promise<void> {
        // Generate new level after finishing previous level
        const tablet: HTMLDivElement = this.shadowRoot!.getElementById("tablet") as HTMLDivElement;
        tablet.innerHTML = "";

        if (this._level === 6) {
            tablet.innerHTML = `
            <p class="victory">Victory!</p>
            `;

            // Update the tablet state in the backend
            const newState: GameState | undefined = await this._gameRouteService.finishedTablet(true);
            if (!newState) {
                return;
            }

            this.updateGameState(newState);
            return;
        }

        this._level++;
        this._sequence = [];
        this.generateLevel();
        return;
    }

    /**
     * Handles cell clicks.
     * @param i - The index of the clicked cell.
     * @param sequenceCell - Whether the clicked cell is part of the sequence.
     * @param cell - The clicked HTML element.
     */
    private handleClick(i: number, sequenceCell: boolean, cell: HTMLDivElement): void {
        if (sequenceCell) {
            // Restart level if player pressed the cells in the wrong sequence
            if (this._sequence[0] !== i) {
                this.restartLevel();
                return;
            }

            // Check if the first cell of the list was pressed to remove visible numbers
            if (this._sequence.indexOf(i) + 1 === 1) {
                const cells: NodeListOf<HTMLDivElement> = this.shadowRoot!.querySelectorAll(".cell");
                cells.forEach(cell => {
                    cell.innerHTML = "";
                });
            }
            // Change cell background to indicate it has been pressed
            cell.style.backgroundColor = "white";

            this._sequence.shift();
            if (this._sequence.length === 0) {
                setTimeout(async () => {
                    await this.nextLevel();
                }, 500);
            }
            return;
        }

        // Restart level if the pressed cell is not a sequenceCell
        this.restartLevel();
        return;
    }

    /**
     * Generates a random sequence of positions for the level.
     * @param level - The current level of the game.
     * @returns {number[]} An array containing the randomly chosen sequence positions.
     */
    private generateSequence(level: number): number[] {
        const positions: number[] = [];
        for (let i: number = 0; i < 12; i++) {
            positions.push(i);
        }
        positions.sort(() => Math.random() - 0.5);
        return positions.slice(0, level);
    }

    /**
     * Generates the entire level and initializes the level sequence.
     */
    private generateLevel(): void {
        // Display current level
        const levelDisplay: HTMLParagraphElement = this.shadowRoot!.getElementById("level") as HTMLParagraphElement;
        levelDisplay.innerHTML = `Level ${this._level}`;

        // Generate sequence
        this._sequence = this.generateSequence(this._level);

        const tablet: HTMLDivElement = this.shadowRoot!.getElementById("tablet") as HTMLDivElement;

        // Generate the cells the player needs to press
        for (let i: number = 0; i < 12; i++) {
            const cell: HTMLDivElement = document.createElement("div");
            cell.classList.add("cell");
            if (this._sequence.includes(i)) {
                cell.addEventListener("click", () => this.handleClick(i, true, cell));
                cell.innerHTML = `<h1 class="sequence-number">${this._sequence.indexOf(i) + 1}</h1>`;
            }
            else {
                cell.addEventListener("click", () => this.handleClick(i, false, cell));
            }
            tablet.appendChild(cell);
        }
    }
}
